import { Stack, Typography } from '@mui/material';
import { useMemo, useState } from 'react';

import { SubpageLayout } from '@/components/architecture/Layout/SubpageLayout';
import { ButtonGroup } from '@/components/form/baseInputs/ButtonGroup';
import { Link } from '@/components/form/baseInputs/Link';
import { Callout } from '@/components/notifications/Callout/Callout';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { DataTableCard } from '@/components/tables/DataTable/components/DataTableCard';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';

import {
  FeatureFlagRow,
  getReleaseGroupsFromQuery,
  GROUP_BY_OPTIONS,
  GroupByField,
  groupRows,
  mapEvaluationToRow,
} from './AdminFeatureFlagsPage.utils';
import { FeatureFlagControlsDrawer } from './FeatureFlagControlsDrawer';
import { FeatureFlagsTable } from './FeatureFlagsTable';
import { useAdminFeatureFlagsPageQuery } from './graphql/AdminFeatureFlagsPage.generated';

export function AdminFeatureFlagsPage() {
  const trackUserEvent = useTrackUserEvent();
  const { showFeedback } = useFeedback();
  const { data, loading, error } = useAdminFeatureFlagsPageQuery({
    fetchPolicy: 'cache-and-network',
    onError: () => {
      showFeedback(
        "We weren't able to load feature flags. Please refresh the page and try again."
      );
    },
  });

  useReportError('failed to load feature flags page', error);

  const [groupBy, setGroupBy] = useState<GroupByField>('group');
  const [selectedFlagId, setSelectedFlagId] = useState<string | null>(null);

  const rows: FeatureFlagRow[] = useMemo(() => {
    return (data?.featureFlagEvaluationResult ?? []).map(mapEvaluationToRow);
  }, [data?.featureFlagEvaluationResult]);

  const releaseGroups = useMemo(
    () => getReleaseGroupsFromQuery(data?.releaseGroups?.edges),
    [data?.releaseGroups?.edges]
  );
  const releaseGroupTotalCount = data?.releaseGroups?.totalCount ?? 0;
  const hasMoreReleaseGroups =
    data?.releaseGroups?.pageInfo?.hasNextPage ?? false;

  const sections = useMemo(() => groupRows(rows, groupBy), [rows, groupBy]);

  const selectedRow = useMemo(
    () => rows.find((r) => r.id === selectedFlagId) ?? null,
    [rows, selectedFlagId]
  );

  return (
    <SubpageLayout
      heading="Feature flags"
      actions={
        <ButtonGroup
          label=""
          options={GROUP_BY_OPTIONS}
          value={groupBy}
          onChange={(_, value) => {
            setGroupBy(value);
            trackUserEvent('feature_flag grouping_changed', { groupBy: value });
          }}
        />
      }
    >
      <Stack spacing={3}>
        <Callout severity="info-high">
          <p>Notes:</p>
          <ul>
            <li>
              The feature flag cache only refreshes every 30 seconds. Changes
              may take up to 30 seconds to take effect (refresh the app
              locally).
            </li>
            <li>
              You cannot move directly from &quot;On for all&quot; to a more
              specific scope (tenant or release group) — turn the flag off
              first, then re-enable for the narrower scope.
            </li>
            <li>Click a row to open the controls drawer for that flag.</li>
          </ul>
        </Callout>

        <DataTableCard>
          <Stack spacing={3}>
            {groupBy === 'group' && sections.length > 0 && (
              <Stack
                component="nav"
                aria-label="Feature flag groups"
                spacing={1}
              >
                <Typography variant="title-s">Jump to group</Typography>
                <Stack direction="row" flexWrap="wrap" gap={2}>
                  {sections.map((section) => (
                    <Link
                      key={section.key}
                      display={section.label}
                      href={`#feature-flag-group-${section.key}`}
                    />
                  ))}
                </Stack>
              </Stack>
            )}
            {sections.map((section) => (
              <Stack
                key={section.key}
                id={`feature-flag-group-${section.key}`}
                spacing={1}
                sx={{ scrollMarginTop: 3 }}
              >
                <Typography variant="title-s">{section.label}</Typography>
                <FeatureFlagsTable
                  rows={section.rows}
                  loading={loading}
                  groupBy={groupBy}
                  onRowClick={(row) => setSelectedFlagId(row.id)}
                />
              </Stack>
            ))}
            {!loading && sections.length === 0 && (
              <Typography variant="body-xs">No feature flags found.</Typography>
            )}
          </Stack>
        </DataTableCard>
      </Stack>

      {selectedRow && (
        <FeatureFlagControlsDrawer
          row={selectedRow}
          releaseGroups={releaseGroups}
          hasMoreReleaseGroups={hasMoreReleaseGroups}
          releaseGroupTotalCount={releaseGroupTotalCount}
          isOpen
          onClose={() => setSelectedFlagId(null)}
        />
      )}
    </SubpageLayout>
  );
}
